/* global runtime */
// Årsregnskapsmodell, noter og fremdrift.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const annualAccountsStageIndex = (stage) => {
  const stages = ['review', 'documents', 'signing', 'processing', 'done'];
  return Math.max(0, stages.indexOf(stage));
};
const annualAccountsStageName = (index) => ['review', 'documents', 'signing', 'processing', 'done'][Math.max(0, Math.min(4, num(index, 0)))] || 'review';

const annualAccountsYesNo = (value) => {
  const clean = String(value || '').trim().toLowerCase();
  if (clean === 'ja' || clean === 'yes') return 'ja';
  if (clean === 'nei' || clean === 'no') return 'nei';
  return '';
};

const annualAccountsYesNoLabel = (value) => {
  const clean = annualAccountsYesNo(value);
  if (clean === 'ja') return t('common.yes');
  if (clean === 'nei') return t('common.no');
  return '-';
};

const annualAccountsRowsInRange = (rows, from, to, { absolute = false } = {}) =>
  safeArray(rows)
    .filter((row) => isTrialAccountInRange(row, from, to))
    .map((row) => ({
      code: row.code,
      label: String(row.label || '').replace(new RegExp(`^${row.code}\\s*-\\s*`), '') || t('clo.account', 'Konto {{code}}', { code: row.code }),
      amount: Math.round((absolute ? Math.abs(row.amount) : row.amount) * 100) / 100,
    }))
    .filter((row) => Math.abs(row.amount) > 0.005);

const annualAccountsRowsMatching = (rows, predicate, { absolute = false, sign = 1 } = {}) =>
  safeArray(rows)
    .filter(predicate)
    .map((row) => ({
      code: row.code,
      label: String(row.label || '').replace(new RegExp(`^${row.code}\\s*-\\s*`), '') || t('clo.account', 'Konto {{code}}', { code: row.code }),
      amount: Math.round((absolute ? Math.abs(row.amount) : row.amount * sign) * 100) / 100,
    }))
    .filter((row) => Math.abs(row.amount) > 0.005);

const annualAccountsSumRows = (rows) =>
  Math.round(safeArray(rows).reduce((sum, row) => sum + num(row.amount), 0) * 100) / 100;

const annualAccountsTaxCostRow = (row) => {
  const nr = Number(row.code);
  const label = String(row.label || '').toLowerCase();
  return (nr >= 8300 && nr <= 8399) ||
    /\b(skattekostnad|skatt på årets resultat|skat af årets resultat|tax.*result)\b/i.test(label);
};

const annualAccountsBalanceGroup = (rows, key, label, predicate, options = {}) => {
  const items = annualAccountsRowsMatching(rows, predicate, options);
  return {
    key,
    label,
    rows: items,
    total: annualAccountsSumRows(items),
  };
};

const annualAccountsNoteAmount = (rows) => annualAccountsSumRows(rows);

const annualAccountsProcessBoolean = (value, fallback = false) =>
  value === undefined ? fallback : Boolean(value);

const annualAccountsWorkYears = (payroll, process) => {
  if (Number.isFinite(Number(process?.workYears))) return Math.max(0, Math.round(Number(process.workYears) * 100) / 100);
  return Math.round(safeArray(payroll?.overview).reduce((sum, employee) => sum + num(employee.fte), 0) * 100) / 100;
};

const annualAccountsNoteLine = (label, amount) => ({
  label,
  amount: Math.round(num(amount) * 100) / 100,
});

const buildAnnualAccountsModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const reports = cachedYearReports(state, year);
  const company = buildCompany(state);
  const payroll = derivePayroll(state);
  const companyType = settingsCompanyType(state.settings);
  const isEnk = isEnkCompanyType(companyType);
  const accountingScope = resolveAccountingScope(state.settings, year);
  const yearKey = String(year);
  const process = state.annualAccountsProcessByYear?.[yearKey] || {};
  const importedSubmitted = importedComplianceDelivered(state, year, 'annualAccounts');
  const deadlineStatus = state.deadlinesByYear?.[yearKey] || {};
  const allRows = options.trialRows || trialAccountRows(reports);
  const intangibleAssetGroup = annualAccountsBalanceGroup(allRows, 'intangible-assets', t('clo.balanceGroup.intangibleAssets', 'Immaterielle eiendeler'), (row) => isTrialAccountInRange(row, 1000, 1099));
  const fixedOperatingAssetGroup = annualAccountsBalanceGroup(allRows, 'fixed-operating-assets', t('clo.balanceGroup.fixedOperatingAssets', 'Varige driftsmidler'), (row) => isTrialAccountInRange(row, 1100, 1299));
  // A debit balance on 2299 is presented as a long-term receivable in the filed accounts.
  const longTermReceivable = (row) => /^2299(?::|$)/.test(String(row.code)) && num(row.amount) > 0.005;
  const longTermReceivableAmount = annualAccountsSumRows(annualAccountsRowsMatching(allRows, longTermReceivable));
  const financialFixedAssetRows = [
    ...allRows.filter((row) => isTrialAccountInRange(row, 1300, 1399)),
    ...(longTermReceivableAmount > 0.005 ? [{ code: '2299:receivable', label: t('clo.balanceGroup.otherLongTermReceivables', 'Andre langsiktige fordringer'), amount: longTermReceivableAmount }] : []),
  ];
  const financialFixedAssetGroup = annualAccountsBalanceGroup(financialFixedAssetRows, 'financial-fixed-assets', t('clo.balanceGroup.financialFixedAssets', 'Finansielle anleggsmidler'), () => true);
  const fixedAssetGroups = [intangibleAssetGroup, fixedOperatingAssetGroup, financialFixedAssetGroup].filter((group) => group.rows.length);
  const inventoryGroup = annualAccountsBalanceGroup(allRows, 'inventory', t('nav.inventory'), (row) => isTrialAccountInRange(row, 1400, 1499));
  // A debit balance on a shareholder/employee settlement account is a receivable.
  // Norwegian VAT settlement and owner clearing debit balances are current receivables.
  const settlementReceivable = (row) => accountingScope.jurisdiction === 'NO' && /^(?:2740|2915)(?::|$)/.test(String(row.code)) && num(row.amount) > 0.005;
  const ownerReceivable = (row) => /^2930(?::|$)/.test(String(row.code)) && num(row.amount) > 0.005;
  const ownerReceivableAmount = annualAccountsSumRows(annualAccountsRowsMatching(allRows, ownerReceivable));
  const receivablePresentationRows = [
    ...allRows.filter((row) => isTrialAccountInRange(row, 1500, 1699)),
    ...allRows.filter(settlementReceivable).map((row) => ({ ...row, label: /^2740/.test(String(row.code)) ? t('clo.balanceGroup.vatReceivable', 'MVA til gode') : t('clo.balanceGroup.debitReceivable', 'Fordring – debetsaldo {{code}}', { code: row.code }) })),
    ...(ownerReceivableAmount > 0.005 ? [{ code: '2930:receivable', label: t('clo.balanceGroup.otherCurrentReceivables', 'Andre kortsiktige fordringer'), amount: ownerReceivableAmount }] : []),
  ];
  const receivablesGroup = annualAccountsBalanceGroup(receivablePresentationRows, 'receivables', t('clo.balanceGroup.receivables', 'Fordringer'), () => true);
  const bankCashGroup = annualAccountsBalanceGroup(allRows, 'bank-cash', t('clo.balanceGroup.bankCash', 'Bankinnskudd, kontanter o.l.'), (row) => isTrialAccountInRange(row, 1900, 1999));
  const otherCurrentAssetGroup = annualAccountsBalanceGroup(allRows, 'other-current-assets', t('clo.balanceGroup.otherCurrentAssets', 'Andre omløpsmidler'), (row) => isTrialAccountInRange(row, 1700, 1899));
  const currentAssetGroups = [inventoryGroup, receivablesGroup, otherCurrentAssetGroup, bankCashGroup].filter((group) => group.rows.length);
  const fixedAssets = fixedAssetGroups.flatMap((group) => group.rows);
  const currentAssets = currentAssetGroups.flatMap((group) => group.rows);
  const shareCapitalGroup = annualAccountsBalanceGroup(allRows, 'share-capital', t('clo.balanceGroup.shareCapital', 'Aksjekapital (selskapskapital)'), (row) => isTrialAccountInRange(row, 2000, 2009), { sign: -1 });
  const retainedEquityGroup = annualAccountsBalanceGroup(allRows, 'retained-equity', t('clo.balanceGroup.retainedEquity', 'Opptjent egenkapital / udekket tap'), (row) => isTrialAccountInRange(row, 2010, 2099), { sign: -1 });
  const equityGroups = [shareCapitalGroup, retainedEquityGroup].filter((group) => group.rows.length);
  const longTermDebtGroup = annualAccountsBalanceGroup(allRows, 'long-term-debt', t('clo.balanceGroup.longTermDebt', 'Annen langsiktig gjeld'), (row) => isTrialAccountInRange(row, 2100, 2299) && !longTermReceivable(row), { sign: -1 });
  const shortTermDebtGroup = annualAccountsBalanceGroup(allRows, 'short-term-debt', t('clo.balanceGroup.shortTermDebt', 'Kortsiktig gjeld'), (row) => isTrialAccountInRange(row, 2300, 2999) && !ownerReceivable(row) && !settlementReceivable(row), { sign: -1 });
  const debtGroups = [longTermDebtGroup, shortTermDebtGroup].filter((group) => group.rows.length);
  const equityRows = equityGroups.flatMap((group) => group.rows);
  const debtRows = debtGroups.flatMap((group) => group.rows);
  const isGroupRelatedRow = (row) => {
    const nr = Number(row.code);
    const label = String(row.label || '').toLowerCase();
    return label.includes('konsern') || label.includes('samme gruppe') || label.includes('morselskap') || label.includes('datterselskap') ||
      (nr >= 1320 && nr <= 1329) || (nr >= 1560 && nr <= 1569) || (nr >= 2260 && nr <= 2269);
  };
  const groupReceivableRows = annualAccountsRowsMatching(
    allRows,
    (row) => isGroupRelatedRow(row) && (isTrialAccountInRange(row, 1300, 1399) || isTrialAccountInRange(row, 1500, 1699)),
  );
  const groupLongTermDebtRows = annualAccountsRowsMatching(
    allRows,
    (row) => isGroupRelatedRow(row) && isTrialAccountInRange(row, 2200, 2299),
    { sign: -1 },
  );
  const groupShortTermDebtRows = annualAccountsRowsMatching(
    allRows,
    (row) => isGroupRelatedRow(row) && isTrialAccountInRange(row, 2300, 2999),
    { sign: -1 },
  );
  const incomeRows = annualAccountsRowsMatching(allRows, (row) => isTrialAccountInRange(row, 3000, 3999), { sign: -1 });
  const salesIncomeRows = annualAccountsRowsMatching(allRows, (row) => isTrialAccountInRange(row, 3000, 3799), { sign: -1 });
  const otherOperatingIncomeRows = annualAccountsRowsMatching(allRows, (row) => isTrialAccountInRange(row, 3800, 3999), { sign: -1 });
  const productCostRows = annualAccountsRowsInRange(allRows, 4000, 4999);
  const payrollCostRows = annualAccountsRowsInRange(allRows, 5000, 5999);
  const depreciationRows = annualAccountsRowsInRange(allRows, 6000, 6099);
  const otherOperatingCostRows = annualAccountsRowsMatching(
    allRows,
    (row) => isTrialAccountInRange(row, 6100, 7999),
  );
  const costRows = [...productCostRows, ...payrollCostRows, ...depreciationRows, ...otherOperatingCostRows];
  const financeIncomeRows = annualAccountsRowsMatching(allRows, (row) => isTrialAccountInRange(row, 8000, 8099), { sign: -1 });
  const taxRows = annualAccountsRowsMatching(allRows, annualAccountsTaxCostRow);
  const taxCodes = new Set(taxRows.map((row) => row.code));
  const financeCostRows = annualAccountsRowsMatching(
    allRows,
    (row) => isTrialAccountInRange(row, 8100, 8299) && !taxCodes.has(row.code),
  );
  const operatingIncome = annualAccountsSumRows(incomeRows);
  const operatingCosts = annualAccountsSumRows(costRows);
  const operatingResult = Math.round((operatingIncome - operatingCosts) * 100) / 100;
  const netFinance = Math.round((annualAccountsSumRows(financeIncomeRows) - annualAccountsSumRows(financeCostRows)) * 100) / 100;
  const resultBeforeTax = Math.round((operatingResult + netFinance) * 100) / 100;
  const taxCost = annualAccountsSumRows(taxRows);
  const annualResult = Math.round((resultBeforeTax - taxCost) * 100) / 100;
  const totalAssets = annualAccountsSumRows([...fixedAssets, ...currentAssets]);
  const totalEquity = annualAccountsSumRows(equityRows);
  const totalDebt = annualAccountsSumRows(debtRows);
  const preparedByAccountant = annualAccountsYesNo(process.preparedByAccountant);
  const assistedByAccountant = annualAccountsYesNo(process.assistedByAccountant);
  const auditorRequired = annualAccountsYesNo(process.auditorRequired);
  const submissionStage = importedSubmitted ? 'done' : (process.submissionStage || 'review');
  const confirmedStepIds = importedSubmitted
    ? IMPORTED_ANNUAL_ACCOUNTS_STEP_IDS
    : safeArray(process.confirmedStepIds).map(String).filter(Boolean);
  const defaultPeriod = `01.01.${year} - 31.12.${year}`;
  const representativeName = process.representativeName || state.settings?.contactName || runtime.userProfile?.displayName || runtime.user?.displayName || company.name;
  const contactEmail = process.contactEmail || state.settings?.contactEmail || runtime.userProfile?.email || runtime.user?.email || company.email;
  const accountingRuleType = process.accountingRuleType || accountingScope.accountingFramework;
  const previousReports = cachedYearReports(state, year - 1);
  const previousRows = trialAccountRows(previousReports);
  const previousDebitReceivables = annualAccountsSumRows(previousRows.filter((row) => longTermReceivable(row) || ownerReceivable(row) || settlementReceivable(row)));
  const automaticComparatives = { operatingIncome: annualAccountsSumRows(annualAccountsRowsMatching(previousRows, (row) => isTrialAccountInRange(row, 3000, 3999), { sign: -1 })), operatingCosts: annualAccountsSumRows(annualAccountsRowsInRange(previousRows, 4000, 7999)), annualResult: num(previousReports?.result?.resultat), totalAssets: annualAccountsSumRows(annualAccountsRowsInRange(previousRows, 1000, 1999)) + previousDebitReceivables, totalEquity: annualAccountsSumRows(annualAccountsRowsMatching(previousRows, (row) => isTrialAccountInRange(row, 2000, 2099), { sign: -1 })), totalDebt: annualAccountsSumRows(annualAccountsRowsMatching(previousRows, (row) => isTrialAccountInRange(row, 2100, 2999), { sign: -1 })) + previousDebitReceivables };
  const comparativeFigures = { ...automaticComparatives, ...(process.comparativeFigures || {}) };
  const annualNoteRuleContext = {
    year,
    accountingScope,
    process: { ...process, accountingRuleType },
    trialRows: allRows,
    payrollCostRows,
    tangibleAssets: fixedOperatingAssetGroup.rows,
    hasTangibleActivity: previousRows.some((row) => isTrialAccountInRange(row, 1100, 1299)),
    groupRows: [...groupReceivableRows, ...groupLongTermDebtRows, ...groupShortTermDebtRows],
    shareholders: safeArray(state.shareholders),
  };
  const noteDecisions = window.PlummeAnnualNoteRules?.resolve(annualNoteRuleContext) || [];
  return {
    year,
    company,
    companyType: accountingScope.legalFormCode,
    isEnk,
    accountingScope,
    reviewRequired: accountingScope.annualAccountsApplicability === 'review_required' || accountingScope.productSupport === 'not_implemented',
    annualAccountsRequired: accountingScope.annualAccountsApplicability === 'applicable'
      ? true
      : accountingScope.entityFamily === 'sole_trader' || accountingScope.annualAccountsApplicability === 'not_applicable'
        ? false
        : null,
    status: {
      yearClosed: Boolean(state.closedYearsByYear?.[yearKey] || state.yearClosingProcessByYear?.[yearKey]?.closedAt || deadlineStatus.annualClosingSubmitted),
      submitted: Boolean(deadlineStatus.annualAccountsSubmitted || process.submittedAt),
    },
    process: {
      ...process,
      submissionStage,
      stageIndex: annualAccountsStageIndex(submissionStage),
      activeStepId: importedSubmitted ? 'all' : (process.activeStepId || 'about'),
      confirmedStepIds,
      preparedByAccountant,
      assistedByAccountant,
      auditorRequired,
      annualAccountsDocumentPrepared: importedSubmitted || Boolean(process.annualAccountsDocumentPrepared),
      boardProtocolPrepared: importedSubmitted || Boolean(process.boardProtocolPrepared),
      generalAssemblyProtocolPrepared: importedSubmitted || Boolean(process.generalAssemblyProtocolPrepared),
      // Signert per dokument. Eldre foretak har bare den samlede flaggen, så den brukes som fallback.
      annualAccountsDocumentSigned: importedSubmitted || Boolean(process.annualAccountsDocumentSigned ?? process.annualAccountsSigned),
      boardProtocolSigned: importedSubmitted || Boolean(process.boardProtocolSigned ?? process.annualAccountsSigned),
      generalAssemblyProtocolSigned: importedSubmitted || Boolean(process.generalAssemblyProtocolSigned ?? process.annualAccountsSigned),
      annualAccountsSigned: importedSubmitted || Boolean(process.annualAccountsSigned),
    },
    submission: {
      companyLabel: company.orgnr ? `${company.name} (${company.orgnr})` : company.name,
      contactEmail,
      accountingPeriod: process.accountingPeriod || defaultPeriod,
      accountingYear: year,
      representativeName,
      accountingRuleType,
      preparedByAccountantLabel: annualAccountsYesNoLabel(preparedByAccountant),
      assistedByAccountantLabel: annualAccountsYesNoLabel(assistedByAccountant),
      auditorRequiredLabel: annualAccountsYesNoLabel(auditorRequired),
    },
    comparativeFigures,
    noteDecisions,
    annualNoteRuleContext,
    trialRows: allRows,
    previousTrialRows: previousRows,
    incomeStatement: {
      incomeRows,
      salesIncomeRows,
      otherOperatingIncomeRows,
      costRows,
      productCostRows,
      payrollCostRows,
      depreciationRows,
      otherOperatingCostRows,
      financeIncomeRows,
      financeCostRows,
      taxRows,
      operatingIncome,
      operatingCosts,
      operatingResult,
      netFinance,
      resultBeforeTax,
      taxCost,
      annualResult,
      dispositionRows: annualAccountsRowsMatching(allRows, (row) => isTrialAccountInRange(row, 8900, 8999)),
    },
    balanceSheet: {
      fixedAssetGroups,
      fixedAssets,
      currentAssetGroups,
      currentAssets,
      equityGroups,
      equityRows,
      debtGroups,
      debtRows,
      totalFixedAssets: annualAccountsSumRows(fixedAssets),
      totalCurrentAssets: annualAccountsSumRows(currentAssets),
      totalAssets,
      totalEquity,
      totalDebt,
      totalEquityAndDebt: Math.round((totalEquity + totalDebt) * 100) / 100,
      difference: Math.round((totalAssets - totalEquity - totalDebt) * 100) / 100,
    },
    notes: window.PlummeAnnualNotes.build({
      companyType,
      isEnk,
      process,
      payroll,
      intangibleAssets: intangibleAssetGroup.rows.filter((row)=>String(row.code).slice(0,4)!=='1070'),
      hasIntangibleActivity: previousRows.some((row)=>isTrialAccountInRange(row,1000,1099)&&String(row.code).slice(0,4)!=='1070'),
      tangibleAssets: fixedOperatingAssetGroup.rows,
      hasTangibleActivity: previousRows.some((row) => isTrialAccountInRange(row, 1100, 1299)),
      depreciationRows,
      locale: window.PlummeI18n?.getLocale?.(),
      payrollCostRows,
      retainedEquityRows: retainedEquityGroup.rows,
      groupReceivableRows,
      groupLongTermDebtRows,
      groupShortTermDebtRows,
      trialRows: allRows,
      shareholders: safeArray(state.shareholders),
      noteDecisions,
      annualResult,
      totalEquity,
    }),
  };
};

const saveAnnualAccountsProgress = async ({ year, patch = {} } = {}) => {
  const yearKey = String(Number(year || runtime.appState?.activeYear) || new Date().getFullYear());
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  if (!isAccountingYearClosedForYear(runtime.appState, yearKey)) throw new Error(t('r1e.annual.yearEndRequired', { year: yearKey }));
  const result = await authorizedPost('annualAccountsMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'save_progress',
    year: Number(yearKey),
    patch,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return getAnnualAccountsModel({ year: Number(yearKey) });
};
